import type { JSONContent } from "@tiptap/react";

import {
  type DefaultHeaderInfo,
  mergeHeaderWithBody,
  wrapInRow,
} from "@/components/email-composer/converter/merge-header";
import type {
  EmailButtonNode,
  EmailParagraphNode,
  EmailTopLevelNode,
  TiptapEmailJSON,
} from "@/components/email-composer/types/tiptap-json";

import { isColorDark } from "@/lib/color";
import {
  buildFormSuiteUrl,
  buildFormUrl,
  replaceFormPlaceholders,
} from "@/lib/form-message-utils";
import { getWindowBaseURL } from "@/lib/get-base-url";

import type { FormTable } from "@/types/forms/form";
import type { UserTiny } from "@/types/user";

type FormEmailPatient = Pick<UserTiny, "firstName" | "lastName">;

function makeParagraph(text?: string): EmailParagraphNode {
  if (!text) {
    return { type: "emailParagraph", attrs: { textAlign: "left" } };
  }
  return {
    type: "emailParagraph",
    attrs: { textAlign: "left" },
    content: [{ type: "text", text }],
  };
}

function makeButton(
  href: string,
  buttonColor: string,
  text = "Fill Out Form",
): EmailButtonNode {
  const textColor = isColorDark(buttonColor) ? "#ffffff" : "#000000";
  return {
    type: "emailButton",
    attrs: {
      text,
      href,
      backgroundColor: buttonColor,
      textColor,
      alignment: "center",
      borderRadius: "lg",
    },
  } as EmailButtonNode;
}

export function convertFormTemplateToEmailBody(
  template: string | null | undefined,
  patient: FormEmailPatient,
  form: FormTable,
  formUrl: string,
  buttonColor: string,
): EmailTopLevelNode[] {
  // Default template if none exists
  if (!template) {
    const formText = form.name.toLowerCase().includes("form")
      ? form.name
      : `${form.name} form`;

    return [
      wrapInRow(
        makeParagraph(
          `Hi ${patient.firstName}, please fill out the ${formText}.`,
        ),
      ),
      wrapInRow(makeParagraph()),
      wrapInRow(makeButton(formUrl, buttonColor)),
    ];
  }

  // Replace patient/form name placeholders (but NOT url placeholders yet)
  const withPlaceholders = replaceFormPlaceholders(
    template,
    patient,
    form.name,
  );

  // Split by URL placeholders — these become button nodes
  const urlPattern = /\{(?:form\.)?url\}/gi;
  const parts = withPlaceholders.split(urlPattern);
  const hasUrlPlaceholder = parts.length > 1;

  const nodes: EmailTopLevelNode[] = [];

  parts.forEach((part, index) => {
    // Convert each text segment into paragraph rows (split by newlines)
    const lines = part.split("\n");
    for (const line of lines) {
      nodes.push(wrapInRow(makeParagraph(line || undefined)));
    }

    // After each part (except the last), insert a button node
    if (index < parts.length - 1) {
      nodes.push(wrapInRow(makeButton(formUrl, buttonColor)));
    }
  });

  // Always include the button, even when the template omits the URL placeholder
  if (!hasUrlPlaceholder) {
    nodes.push(wrapInRow(makeParagraph()));
    nodes.push(wrapInRow(makeButton(formUrl, buttonColor)));
  }

  return nodes;
}

/**
 * Builds a complete email document with header + form body + button.
 */
export function buildFormEmailDocument(
  headerJson: JSONContent | null | undefined,
  patient: FormEmailPatient,
  form: FormTable,
  tenantSubDomain: string | undefined,
  buttonColor: string,
  defaultHeader?: DefaultHeaderInfo,
  membershipId?: string,
): TiptapEmailJSON {
  const formUrl = buildFormUrl(tenantSubDomain, form.pathName, membershipId);
  const bodyNodes = convertFormTemplateToEmailBody(
    form.defaultSendingText,
    patient,
    form,
    formUrl,
    buttonColor,
  );

  return mergeHeaderWithBody(headerJson, bodyNodes, defaultHeader);
}

/**
 * Builds an email document for one or more forms.
 *
 * - 1 form: delegates to buildFormEmailDocument (preserves custom template support)
 * - N forms: builds greeting, numbered list, and a single grouped URL button
 * - With appointmentId: uses appointment URL (/appointment/{id}/forms)
 */
export function buildFormsEmailDocument(
  headerJson: JSONContent | null | undefined,
  patient: FormEmailPatient,
  forms: FormTable[],
  tenantSubDomain: string | undefined,
  buttonColor: string,
  appointmentId?: string,
  defaultHeader?: DefaultHeaderInfo,
  membershipId?: string,
): TiptapEmailJSON {
  if (forms.length === 1 && !appointmentId) {
    return buildFormEmailDocument(
      headerJson,
      patient,
      forms[0],
      tenantSubDomain,
      buttonColor,
      defaultHeader,
      membershipId,
    );
  }

  const formsUrl = appointmentId
    ? `${getWindowBaseURL(tenantSubDomain)}/appointment/${appointmentId}/forms`
    : buildFormSuiteUrl(
        tenantSubDomain,
        forms.map((f) => f.pathName),
        membershipId,
      );

  const bodyNodes: EmailTopLevelNode[] = [
    wrapInRow(
      makeParagraph(
        `Hi ${patient.firstName}, please complete the following forms:`,
      ),
    ),
    wrapInRow(makeParagraph()),
  ];

  for (let i = 0; i < forms.length; i++) {
    bodyNodes.push(wrapInRow(makeParagraph(`${i + 1}. ${forms[i].name}`)));
  }

  bodyNodes.push(wrapInRow(makeParagraph()));
  bodyNodes.push(
    wrapInRow(makeButton(formsUrl, buttonColor, "Fill Out Forms")),
  );

  return mergeHeaderWithBody(headerJson, bodyNodes, defaultHeader);
}
